import React from 'react';
import { Dialog, DialogContent, DialogActions, Button, Typography, Box, IconButton, CircularProgress } from '@mui/material';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';

const ORANGE = 'var(--brand-primary, #FF6900)';
const MUTED = 'var(--brand-text-muted, #6b6b6b)';
const BORDER = 'var(--brand-border, #e3e0dc)';
const TEXT = 'var(--brand-text, #111827)';

/**
 * "Assign {product}" → "Confirm assignment" — PW deck slides 51-52, the flow
 * behind picking a card in LinkTypePickerDialog (currently just LearnCreator;
 * `LINK_TYPE_TO_PRODUCT_KEY` in utils/constants.js is where more product
 * mappings get added as this flow grows). Two screens for the same reason
 * PublishWorkspaceDialogs splits into steps: the first shows what will
 * happen, the second is a distinct "are you sure" gate before the real side
 * effect (enabling the product + emailing the admin) fires.
 */

const secondaryBtnSx = {
    borderRadius: '20px', minWidth: 100, fontWeight: 700, textTransform: 'none',
    borderColor: '#9e9e9e', color: '#5f5f5f',
};

const primaryBtnSx = {
    borderRadius: '20px', minWidth: 150, fontWeight: 700, textTransform: 'none',
    backgroundColor: ORANGE, color: '#fff', '&:hover': { backgroundColor: '#c9541e' },
};

const ChecklistRow = ({ children }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <Box sx={{
            width: 20, height: 20, borderRadius: '50%', backgroundColor: 'rgba(255,105,0,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '1px',
        }}>
            <CheckIcon sx={{ fontSize: 13, color: ORANGE }} />
        </Box>
        <Typography sx={{ fontSize: '13.5px', color: TEXT, lineHeight: 1.5 }}>{children}</Typography>
    </Box>
);

/**
 * @param {Object} org  { name, isNetwork, subtitle, contactEmail } — built by
 *   the caller from the real client row (org_name / is_school_network /
 *   locationLabel+schools.length / contact_email), not sample data.
 */
export const AssignProductDialog = ({ open, onClose, onProceed, productLabel, org }) => {
    if (!org) return null;
    const orgTypeWord = org.isNetwork ? 'network' : 'school';
    const badge = org.isNetwork ? 'NETWORK' : 'SCHOOL';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
                PaperProps={{ sx: { backgroundColor: 'var(--brand-bg, #FFFBF7)', borderRadius: '20px' } }}>
            <DialogContent sx={{ pt: 3, pb: 2, position: 'relative' }}>
                <IconButton onClick={onClose} aria-label="Close" sx={{ position: 'absolute', top: 12, right: 12, color: MUTED }}>
                    <CloseIcon fontSize="small" />
                </IconButton>

                <Typography sx={{ fontSize: '21px', fontWeight: 800, mb: 2.5 }}>
                    {`Assign ${productLabel}`}
                </Typography>

                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                    border: `1px solid ${BORDER}`, backgroundColor: 'var(--brand-surface, #fff)',
                    borderRadius: '14px', p: 2, mb: 2.5,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Box sx={{
                            width: 48, height: 48, borderRadius: '12px', flexShrink: 0,
                            background: 'linear-gradient(135deg, #FFA24D, #FF6900)',
                        }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: '16px', fontWeight: 800 }}>{org.name}</Typography>
                            <Typography sx={{ fontSize: '12.5px', color: MUTED }}>{org.subtitle}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{
                        backgroundColor: 'rgba(255,105,0,0.12)', color: ORANGE, fontSize: '11px', fontWeight: 800,
                        letterSpacing: '0.5px', textTransform: 'uppercase', px: 1.5, py: 0.5, borderRadius: '20px',
                        flexShrink: 0,
                    }}>
                        {badge}
                    </Box>
                </Box>

                <Typography sx={{ fontSize: '14px', color: MUTED, lineHeight: 1.6, mb: 2.5 }}>
                    {`${productLabel} will be enabled for this ${orgTypeWord}. Their admin gets an email with access details — nothing else to set up.`}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <ChecklistRow>
                        {`${productLabel} is switched on for this ${orgTypeWord} immediately.`}
                    </ChecklistRow>
                    <ChecklistRow>
                        {'An email with access details goes to '}
                        <strong>{org.contactEmail || '—'}</strong>.
                    </ChecklistRow>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx}>
                        Cancel
                    </Button>
                    <Button onClick={onProceed} variant="contained" sx={primaryBtnSx}>
                        Assign & notify
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export const ConfirmAssignmentDialog = ({ open, onCancel, onConfirm, loading = false, productLabel, orgName }) => (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogContent sx={{ pt: 4, pb: 1, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 800, mb: 1.5 }}>
                Confirm assignment
            </Typography>
            <Typography sx={{ fontSize: '14px', color: MUTED, lineHeight: 1.6 }}>
                {`Assign ${productLabel} to `}<strong style={{ color: TEXT }}>{orgName}</strong>{' and notify its admin?'}
            </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1.5, pb: 3, pt: 2 }}>
            <Button onClick={onCancel} disabled={loading} variant="outlined" sx={secondaryBtnSx}>
                Cancel
            </Button>
            <Button
                onClick={onConfirm}
                disabled={loading}
                variant="contained"
                startIcon={loading ? <CircularProgress size={15} color="inherit" /> : null}
                sx={primaryBtnSx}
            >
                {loading ? 'Assigning…' : 'Proceed'}
            </Button>
        </DialogActions>
    </Dialog>
);
